/* eslint-disable no-restricted-syntax */
import { Map } from 'ol';
import { Group } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import Layer from 'ol/layer/Layer';
import {
  useCallback, useContext, useMemo, useRef,
} from 'react';
import { XYZ } from 'ol/source';
import { transform, get } from 'ol/proj';
import type { Projection } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import type { MessageInstance } from 'antd/es/message/interface';
import { Dayjs } from 'dayjs';
import { Product } from 'types/app.types';
import { DTNDisplayRow, DTNTooltip } from 'services/dtn/types';
import { DTN_API } from 'configs/endpoints';
import { dtnTokenManager } from 'services/dtn/auth';
import { purgeLayerImages } from 'utils/imageCaches';
import disposeLayerAfterRender from 'utils/layerLifecycle';
import {
  createDTNTileLayer,
  createDTNTileSource,
  findClosestDTNTimestamp,
  updateDTNTileSetId,
} from '../dtn/createDTNTileSource';
import {
  fetchAndCacheDTNTimestamps,
  getCachedTimestamps,
  getSampledTimestamps,
} from '../dtn/dtnTimestampsCache';
import { getLayerByName } from '../utils';
import LayerName from '../enums';
import zIndexes from '../../../../configs/zIndexes';
import { utcDayjs } from '../../../../utils';
import { LayerManagementAdapter, PopupLayerEntry } from './types';
import TimeSeriesContext from '../../../TimeSeriesContext';

const TRANSPARENT = '#00000000';
const COLOR_FALLBACK = '#F2EFE9';

const normLabel = (s: string) => s.toLowerCase().replace(/\s+/g, '');

/**
 * Derives a CSS color for a given display value from a DTN legend list.
 * labelHint is used to narrow the search to the legend matching this specific row
 * (e.g. "precipitation Type" matches legend label "Precipitation Type").
 * Falls back to trying all legends if no label match is found.
 */
function resolveColorFromLegend(
  legendList: import('services/dtn/types').DTNLegendList,
  value: string | number,
  labelHint?: string,
): string {
  // The API can return numeric values as strings — coerce early so all branches use the same value.
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  const isNumeric = typeof numericValue === 'number' && !Number.isNaN(numericValue);

  // Prefer the legend whose label matches this row; fall back to all legends.
  const hintNorm = labelHint ? normLabel(labelHint) : null;
  // eslint-disable-next-line no-nested-ternary
  const candidates = hintNorm
    ? legendList.find((l) => l.label && normLabel(l.label) === hintNorm)
      ? [legendList.find((l) => l.label && normLabel(l.label) === hintNorm)!]
      : legendList
    : legendList;

  for (const legend of candidates) {
    const { colorTable, colorTableType } = legend;
    if (!colorTable?.length) continue; // eslint-disable-line no-continue

    if (colorTableType === 'range' && isNumeric) {
      const itemsWithValues = colorTable.filter((i) => i.value != null);
      if (!itemsWithValues.length) continue; // eslint-disable-line no-continue

      let found: string | null = null;
      for (const item of itemsWithValues) {
        if (numericValue >= Number(item.value!)) found = item.color;
      }
      // Value is below the minimum threshold — use the lowest color rather
      // than falling back to white.
      if (!found) found = itemsWithValues[0].color;
      if (found && found.toLowerCase() !== TRANSPARENT) return found;
    }

    if (colorTableType === 'discrete' && isNumeric) {
      const withValues = colorTable.filter((i) => i.value != null);
      if (withValues.length) {
        const closest = withValues
          .reduce((best, item) => (Math.abs(Number(item.value!) - numericValue)
          < Math.abs(Number(best.value!) - numericValue)
            ? item
            : best));
        if (closest.color.toLowerCase() !== TRANSPARENT) return closest.color;
      }
    }

    if (colorTableType === 'category') {
      const strValue = typeof value === 'string' ? value : String(value);
      const match = colorTable.find(
        (i) => i.category?.toLowerCase() === strValue.toLowerCase(),
      );
      if (match && match.color.toLowerCase() !== TRANSPARENT) return match.color;
    }
  }
  return COLOR_FALLBACK;
}

// Base names of *Display fields that are metadata, not data values — never shown in the popup.
const TOOLTIP_SKIP_FIELDS = new Set([
  'validTime',
  'validTimeDisplay',
  'type',
  'dataType',
]);

/**
 * Parses DTN tooltip response into display rows.
 * Handles two API response shapes:
 *   New (v2):  [{ attributes: [{ *Display: val, *DisplayUnit: unit }] }]
 *   Legacy:    [{ type: 'Feature', properties: { *Display: val, *DisplayUnit: unit } }]
 */
function parseDTNTooltip(tooltip: DTNTooltip): {
  rows: DTNDisplayRow[];
  primaryValue: number | null;
} {
  const rows: DTNDisplayRow[] = [];
  let primaryValue: number | null = null;

  const processKeyValueMap = (
    map: Record<string, string | number | undefined>,
  ) => {
    Object.keys(map)
      .filter((k) => k.endsWith('Display') && !k.endsWith('DisplayUnit'))
      .forEach((displayKey) => {
        const baseName = displayKey.slice(0, -'Display'.length);
        if (TOOLTIP_SKIP_FIELDS.has(baseName)) return;
        const unitKey = `${displayKey}Unit`;
        const displayValue = map[displayKey];
        if (displayValue === undefined) return;
        const unit = typeof map[unitKey] === 'string'
          ? (map[unitKey] as string)
          : undefined;
        const label = baseName.replace(/([A-Z])/g, ' $1').trim();
        rows.push({ label, displayValue, unit });
        if (primaryValue === null && typeof displayValue === 'number') {
          primaryValue = displayValue;
        }
      });
  };

  tooltip.forEach((feature) => {
    if (feature.attributes?.length) {
      // New attributes[] format
      feature.attributes.forEach((attrObj) => processKeyValueMap(attrObj));
    } else if (feature.properties) {
      // Legacy GeoJSON properties format
      processKeyValueMap(
        feature.properties as Record<string, string | number | undefined>,
      );
    }
  });

  return { rows, primaryValue };
}

function updateGroupLayerOrder(group: Group) {
  const layers = group
    .getLayers()
    .getArray()
    .sort((a, b) => a.get('layerIndex') - b.get('layerIndex'));
  const zIndex = layers.length;
  layers.forEach((layer, i) => {
    layer.setZIndex(zIndexes.GEOLAYERS + zIndex - i);
  });
}

export interface DTNAdapterDeps {
  map: Map;
  message: MessageInstance;
  editProductTime: (index: number, time: Dayjs | undefined) => void;
  setLoadingLayer: (loading: boolean) => void;
  playbackMode: boolean;
  preloadLayerFn: (params: {
    index: number;
    product: Product;
    generatedTimestampList: string[];
  }) => Promise<void>;
}

export function useDTNAdapter(deps: DTNAdapterDeps): LayerManagementAdapter {
  const {
    map,
    message,
    editProductTime,
    setLoadingLayer,
    playbackMode,
    preloadLayerFn,
  } = deps;
  const { range } = useContext(TimeSeriesContext);

  // Tracks product IDs removed before the async addLayer fetch completed.
  const pendingRemovals = useRef(new Set<string>());

  const notifyRemove = useCallback((productId: string) => {
    pendingRemovals.current.add(productId);
  }, []);

  const addLayer = useCallback(
    async (product: Product, time: string) => {
      const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
      if (!geoLayer) return;

      // Clear any stale removal signal so a re-activation always proceeds.
      pendingRemovals.current.delete(product.id);

      try {
        const timestamps = await fetchAndCacheDTNTimestamps(
          product.internalName,
          [range[0], range[1]] as [Dayjs, Dayjs],
        );

        // Race-condition guard: product was deactivated before the fetch completed.
        if (pendingRemovals.current.has(product.id)) {
          pendingRemovals.current.delete(product.id);
          return;
        }

        // Duplicate guard: layer was already added by a concurrent addLayer call.
        const alreadyAdded = geoLayer
          .getLayers()
          .getArray()
          .some((l) => l.get('id') === product.id);
        if (alreadyAdded) return;

        // eslint-disable-next-line no-console
        console.debug('[DTN] addLayer timestamps', {
          layerId: product.internalName,
          count: timestamps.length,
          range: [range[0]?.toISOString(), range[1]?.toISOString()],
        });

        if (timestamps.length === 0) {
          setLoadingLayer(false);
          message.error(
            `No timestamps available for DTN layer '${product.name}'`,
          );
          return;
        }

        const closest = findClosestDTNTimestamp(timestamps, time) ?? timestamps[0];

        const tileLayer = createDTNTileLayer({
          layerId: product.internalName,
          tileSetId: closest.tileSetId,
          opacity: (product.currentOpacity ?? 80) / 100,
        });
        tileLayer.set('name', product.name);
        tileLayer.set('layerIndex', product.index);
        tileLayer.set('id', product.id);
        tileLayer.set('internalName', product.internalName);
        tileLayer.set('unit', product.layer.unit);
        tileLayer.set('provider', 'dtn');
        tileLayer.set('selfManagesTime', true);
        geoLayer.getLayers().push(tileLayer);
        updateGroupLayerOrder(geoLayer);
        editProductTime(product.index, utcDayjs(closest.valid));
      } catch (error) {
        setLoadingLayer(false);
        message.error(`Error loading DTN layer '${product.name}'. ${error}`);
      }
    },
    [editProductTime, map, message, setLoadingLayer, range],
  );

  const replaceLayer = useCallback(
    (product: Product) => {
      const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
      const layersSnapshot = [...geoLayer.getLayers().getArray()];
      layersSnapshot.forEach((layer) => {
        if (layer.get('layerIndex') === product.index) {
          purgeLayerImages(layer);
          geoLayer.getLayers().remove(layer);
          disposeLayerAfterRender(map, layer as Layer);
          addLayer(product, new Date().toISOString());
        }
      });
    },
    [addLayer, map],
  );

  const updateLayerTime = useCallback(
    async (productIndex: number, isoTime: string) => {
      const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
      if (!geoLayer) {
        return;
      }
      const layer = geoLayer
        .getLayers()
        .getArray()
        .find(
          (l) => l.get('layerIndex') === productIndex && l.get('provider') === 'dtn',
        ) as TileLayer<XYZ> | undefined;
      if (!layer) {
        return;
      }

      const layerId = layer.get('internalName') as string;

      // Playback: use the timestamps pre-fetched for the full range during addLayer.
      // Slider/non-playback: fetch for the current range so the cache stays aligned
      // with whatever range the user has selected (not just the latest available run).
      const timestamps = playbackMode
        ? (getSampledTimestamps(layerId) ?? getCachedTimestamps(layerId) ?? [])
        : await fetchAndCacheDTNTimestamps(layerId, [range[0], range[1]] as [Dayjs, Dayjs]);

      const closest = findClosestDTNTimestamp(timestamps, isoTime) ?? timestamps[0];

      if (closest) {
        updateDTNTileSetId(layer, closest.tileSetId);
        editProductTime(productIndex, utcDayjs(closest.valid));
      }
    },
    [editProductTime, map, playbackMode, range],
  );

  const preloadLayer = useCallback(
    (params: {
      index: number;
      product: Product;
      generatedTimestampList: string[];
    }) => preloadLayerFn(params),
    [preloadLayerFn],
  );

  const getLayerTooltip = useCallback(
    async (
      layer: Parameters<LayerManagementAdapter['getLayerTooltip']>[0],
      coordinate: Coordinate | number[],
      destinationProjection: string,
      mapInstance: Map,
    ): Promise<PopupLayerEntry[]> => {
      if (layer.get('provider') !== 'dtn') return [];
      const tileSource = layer.getSource() as XYZ | null;
      const tileSetId = tileSource?.get('tileSetId') as string | undefined;
      const layerId = layer.get('internalName') as string | undefined;
      if (!tileSetId || !layerId) return [];
      try {
        const token = await dtnTokenManager.getToken();
        const wgs84 = transform(
          coordinate as number[],
          get(destinationProjection) as Projection,
          'EPSG:4326',
        );
        const [lng, lat] = wgs84;
        const zoom = Math.floor(mapInstance.getView().getZoom() ?? 5);

        // Fetch tooltip and legend in parallel.
        // Tooltips supports ?token= query param; legends requires Authorization header.
        const [tooltipRes, legendRes] = await Promise.all([
          fetch(
            `${DTN_API}/tooltips/${layerId}/${tileSetId}/${zoom}/${lat}/${lng}?token=${token}`,
            {
              headers: {
                Accept: 'application/json',
                'Accept-Language': 'en-US',
              },
            },
          ),
          fetch(`${DTN_API}/legends/${layerId}`, {
            headers: {
              Accept: 'application/json',
              'Accept-Language': 'en-US',
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        if (!tooltipRes.ok) return [];

        // The API spec says the response is an array, but some layers return a plain object.
        // Normalise to always be an array before parsing.
        const tooltipRaw = await tooltipRes.json();
        const tooltipArray: DTNTooltip = Array.isArray(tooltipRaw)
          ? tooltipRaw
          : [tooltipRaw];

        // The API spec says the legend response is an array, but normalise defensively.
        let legendList: import('services/dtn/types').DTNLegendList = [];
        if (legendRes.ok) {
          const legendRaw = await legendRes.json();
          legendList = Array.isArray(legendRaw) ? legendRaw : [legendRaw];
        }

        const { rows, primaryValue } = parseDTNTooltip(tooltipArray);

        // eslint-disable-next-line no-console
        console.debug('[DTN] getLayerTooltip', {
          layerId,
          legendStatus: legendRes.status,
          legendCount: legendList.length,
          legends: legendList.map((l) => ({
            label: l.label,
            type: l.colorTableType,
            values: l.colorTable.map((i) => i.value ?? i.category),
          })),
          rows,
        });

        if (!rows.length) return [];

        // Check whether any string row selects a legend by name (e.g. "Rain" → Rain legend).
        // When found, numeric rows use that legend instead of trying all of them.
        const typeSelectorLegend = rows
          .filter((r) => typeof r.displayValue === 'string')
          .reduce<import(
'services/dtn/types').DTNLegend | undefined>(
            (found, row) => {
              if (found) return found;
              return legendList.find(
                (l) => l.label
                  && normLabel(l.label) === normLabel(row.displayValue as string),
              );
            },
            undefined,
            );

        const rowsWithColors = rows.map((row) => {
          const numValue = typeof row.displayValue === 'number'
            ? row.displayValue
            : parseFloat(row.displayValue as string);
          const isNumeric = !Number.isNaN(numValue);
          const legendsToSearch = isNumeric && typeSelectorLegend
            ? [typeSelectorLegend] : legendList;
          return {
            ...row,
            color: resolveColorFromLegend(
              legendsToSearch,
              row.displayValue,
              row.label,
            ),
          };
        });

        return [
          {
            label: layer.get('name') as string,
            value: primaryValue,
            unit: rows[0]?.unit || layer.get('unit') || '',
            style: '',
            internalName: layerId,
            provider: 'dtn',
            dtnRows: rowsWithColors,
          },
        ];
      } catch {
        return [];
      }
    },
    [],
  );

  const updateLayerStyle = useCallback(
    (productIndex: number, _styleName: string, filterIds?: string[]) => {
      const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
      const layer = geoLayer
        ?.getLayers()
        .getArray()
        .find((l) => l.get('layerIndex') === productIndex) as
        | TileLayer<XYZ>
        | undefined;
      if (!layer) return;
      const source = layer.getSource();
      const layerId = source?.get('layerId') as string;
      const tileSetId = source?.get('tileSetId') as string;
      if (layerId && tileSetId) {
        layer.setSource(
          createDTNTileSource({
            layerId,
            tileSetId,
            filterIds: filterIds ?? [],
          }),
        );
      }
    },
    [map],
  );

  return useMemo(
    () => ({
      playbackTooltipEnabled: true as const,
      addLayer,
      replaceLayer,
      updateLayerTime,
      notifyRemove,
      preloadLayer,
      getLayerTooltip,
      updateLayerStyle,
    }),
    [
      addLayer,
      replaceLayer,
      updateLayerTime,
      notifyRemove,
      preloadLayer,
      getLayerTooltip,
      updateLayerStyle,
    ],
  );
}
